import { Field, RHF } from "@devhop/ui";
import TextAlign from "@tiptap/extension-text-align";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Code2,
  ImageIcon,
  List,
  ListOrdered,
  Maximize2,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";
import type * as React from "react";
import { useEffect } from "react";
import { ImagePlaceholder } from "../components/ImagePlaceholder";
import { ImageExtension } from "../components/image";

export type ToolbarElement =
  | "bold"
  | "italic"
  | "strikethrough"
  | "code"
  | "bulletList"
  | "orderedList"
  | "blockquote"
  | "codeBlock"
  | "horizontalRule"
  | "hardBreak"
  | "redo"
  | "undo"
  | "image"
  | "alignLeft"
  | "alignCenter"
  | "alignRight"
  | "alignJustify";

export type ToolbarConfig = Partial<Record<ToolbarElement, boolean>>;

const DEFAULT_TOOLBAR_CONFIG: ToolbarConfig = {
  bold: true,
  italic: true,
  alignLeft: true,
  alignCenter: true,
  alignRight: true,
  alignJustify: true,
  bulletList: true,
  orderedList: true,
  undo: true,
  redo: true,
};

type FormTipTapEditorProps = {
  name: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  requiredMark?: boolean;
  className?: string;
  placeholder?: string;
  toolbarElements?: ToolbarElement[] | ToolbarConfig;
};

export function FormTipTapEditor({
  name,
  label,
  hint,
  requiredMark,
  className,
  placeholder = "ເພີ່ມເນື້ອຫາ...",
  toolbarElements,
}: FormTipTapEditorProps) {
  const { Controller, useFormContext } = RHF;
  const { control } = useFormContext();

  // Always start with default config, then merge with any passed elements
  const passedConfig: ToolbarConfig = toolbarElements
    ? Array.isArray(toolbarElements)
      ? Object.fromEntries(toolbarElements.map((el) => [el, true]))
      : toolbarElements
    : {};

  // Merge defaults with passed config (passed values override defaults)
  const toolbarConfig: ToolbarConfig = {
    ...DEFAULT_TOOLBAR_CONFIG,
    ...passedConfig,
  };

  // Convert config object to array of enabled elements (only include if explicitly true)
  const finalToolbarElements: ToolbarElement[] = Object.entries(toolbarConfig)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key as ToolbarElement);

  return (
    <Field name={name} label={label} hint={hint} requiredMark={requiredMark}>
      <Controller
        control={control}
        name={name as never}
        render={({ field }) => (
          <TipTapEditor
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            className={className}
            toolbarElements={finalToolbarElements}
          />
        )}
      />
    </Field>
  );
}

type TipTapEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  toolbarElements?: ToolbarElement[];
};

function TipTapEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  toolbarElements = [
    "bold",
    "italic",
    "alignLeft",
    "alignCenter",
    "alignRight",
    "alignJustify",
    "bulletList",
    "orderedList",
    "undo",
    "redo",
  ],
}: TipTapEditorProps) {
  const finalToolbarElements = toolbarElements;
  const needsImage = finalToolbarElements.includes("image");
  const needsTextAlign = finalToolbarElements.some((el) =>
    ["alignLeft", "alignCenter", "alignRight", "alignJustify"].includes(el),
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        orderedList: finalToolbarElements.includes("orderedList")
          ? {
              HTMLAttributes: {
                class: "list-decimal",
              },
            }
          : false,
        bulletList: finalToolbarElements.includes("bulletList")
          ? {
              HTMLAttributes: {
                class: "list-disc",
              },
            }
          : false,
        code: finalToolbarElements.includes("code")
          ? {
              HTMLAttributes: {
                class: "bg-accent rounded-md p-1",
              },
            }
          : false,
        horizontalRule: finalToolbarElements.includes("horizontalRule")
          ? {
              HTMLAttributes: {
                class: "my-2",
              },
            }
          : false,
        codeBlock: finalToolbarElements.includes("codeBlock")
          ? {
              HTMLAttributes: {
                class:
                  "bg-primary text-primary-foreground p-2 text-sm rounded-md",
              },
            }
          : false,
        heading: {
          levels: [1, 2, 3, 4],
          HTMLAttributes: {
            class: "tiptap-heading",
          },
        },
        hardBreak: finalToolbarElements.includes("hardBreak") ? {} : false,
      }),
      ...(needsTextAlign
        ? [
            TextAlign.configure({
              types: ["heading", "paragraph"],
            }),
          ]
        : []),
      ...(needsImage
        ? [
            ImageExtension,
            ImagePlaceholder.configure({
              HTMLAttributes: {},
              onDrop: (_files, _editor) => {
                // Handle file drop - files are already processed by ImagePlaceholder
                // The component will convert them to base64 and insert as images
              },
              onEmbed: (_url, _editor) => {
                // Handle URL embed - already handled by ImagePlaceholder
              },
              allowedMimeTypes: {
                "image/*": [
                  "image/jpeg",
                  "image/png",
                  "image/gif",
                  "image/webp",
                ],
              },
              maxFiles: 1,
              maxSize: 5 * 1024 * 1024, // 5MB
            }),
          ]
        : []),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onBlur: onBlur ? () => onBlur() : undefined,
    editorProps: {
      attributes: {
        class: "min-h-[200px] p-4 focus:outline-none",
      },
    },
  });

  const handleImageResize = () => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const node = editor.state.doc.nodeAt(from);

    if (node?.type.name === "image") {
      const currentWidth = String(node.attrs.width || "");
      const currentHeight = String(node.attrs.height || "");

      const widthInput = window.prompt(
        "Enter image width (e.g., 300, 50%, 300px, or leave empty for auto):",
        currentWidth,
      );
      const heightInput = window.prompt(
        "Enter image height (e.g., 200, 50%, 200px, or leave empty for auto):",
        currentHeight,
      );

      const attrs: Record<string, unknown> = {};

      if (widthInput?.trim()) {
        attrs.width = widthInput.trim();
      } else if (widthInput === "") {
        attrs.width = null;
      }

      if (heightInput?.trim()) {
        attrs.height = heightInput.trim();
      } else if (heightInput === "") {
        attrs.height = null;
      }

      editor.chain().focus().updateAttributes("image", attrs).run();
    } else {
      window.alert("Please select an image first");
    }
  };

  const handleImageAlign = (align: "left" | "center" | "right") => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const node = editor.state.doc.nodeAt(from);

    if (node?.type.name === "image") {
      editor.chain().focus().updateAttributes("image", { align }).run();
    } else {
      window.alert("Please select an image first");
    }
  };

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const renderToolbarButton = (element: ToolbarElement) => {
    const baseClass =
      "rounded px-2 py-1 text-sm hover:bg-accent disabled:opacity-50 transition-colors";
    const activeClass = "bg-accent";

    switch (element) {
      case "bold":
        return (
          <button
            key="bold"
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`${baseClass} ${
              editor.isActive("bold") ? activeClass : ""
            }`}
            title="Bold"
          >
            <Bold className="h-4 w-4" />
          </button>
        );
      case "italic":
        return (
          <button
            key="italic"
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`${baseClass} ${
              editor.isActive("italic") ? activeClass : ""
            }`}
            title="Italic"
          >
            <em className="font-semibold text-sm">I</em>
          </button>
        );
      case "strikethrough":
        return (
          <button
            key="strikethrough"
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`${baseClass} ${
              editor.isActive("strike") ? activeClass : ""
            }`}
            title="Strikethrough"
          >
            <Strikethrough className="h-4 w-4" />
          </button>
        );
      case "code":
        return (
          <button
            key="code"
            type="button"
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`${baseClass} ${
              editor.isActive("code") ? activeClass : ""
            }`}
            title="Inline Code"
          >
            <Code className="h-4 w-4" />
          </button>
        );
      case "bulletList":
        return (
          <button
            key="bulletList"
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`${baseClass} ${
              editor.isActive("bulletList") ? activeClass : ""
            }`}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </button>
        );
      case "orderedList":
        return (
          <button
            key="orderedList"
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`${baseClass} ${
              editor.isActive("orderedList") ? activeClass : ""
            }`}
            title="Ordered List"
          >
            <ListOrdered className="h-4 w-4" />
          </button>
        );
      case "blockquote":
        return (
          <button
            key="blockquote"
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`${baseClass} ${
              editor.isActive("blockquote") ? activeClass : ""
            }`}
            title="Blockquote"
          >
            <Quote className="h-4 w-4" />
          </button>
        );
      case "codeBlock":
        return (
          <button
            key="codeBlock"
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`${baseClass} ${
              editor.isActive("codeBlock") ? activeClass : ""
            }`}
            title="Code Block"
          >
            <Code2 className="h-4 w-4" />
          </button>
        );
      case "horizontalRule":
        return (
          <button
            key="horizontalRule"
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className={baseClass}
            title="Horizontal Rule"
          >
            <span className="text-sm">─</span>
          </button>
        );
      case "hardBreak":
        return (
          <button
            key="hardBreak"
            type="button"
            onClick={() => editor.chain().focus().setHardBreak().run()}
            className={baseClass}
            title="Hard Break"
          >
            <span className="text-xs">↵</span>
          </button>
        );
      case "undo":
        return (
          <button
            key="undo"
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className={baseClass}
            title="Undo"
          >
            <Undo className="h-4 w-4" />
          </button>
        );
      case "redo":
        return (
          <button
            key="redo"
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className={baseClass}
            title="Redo"
          >
            <Redo className="h-4 w-4" />
          </button>
        );
      case "alignLeft":
        return (
          <button
            key="alignLeft"
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={`${baseClass} ${
              editor.isActive({ textAlign: "left" }) ? activeClass : ""
            }`}
            title="Align Left"
          >
            <AlignLeft className="h-4 w-4" />
          </button>
        );
      case "alignCenter":
        return (
          <button
            key="alignCenter"
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={`${baseClass} ${
              editor.isActive({ textAlign: "center" }) ? activeClass : ""
            }`}
            title="Align Center"
          >
            <AlignCenter className="h-4 w-4" />
          </button>
        );
      case "alignRight":
        return (
          <button
            key="alignRight"
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={`${baseClass} ${
              editor.isActive({ textAlign: "right" }) ? activeClass : ""
            }`}
            title="Align Right"
          >
            <AlignRight className="h-4 w-4" />
          </button>
        );
      case "alignJustify":
        return (
          <button
            key="alignJustify"
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            className={`${baseClass} ${
              editor.isActive({ textAlign: "justify" }) ? activeClass : ""
            }`}
            title="Justify"
          >
            <AlignJustify className="h-4 w-4" />
          </button>
        );
      case "image": {
        const isImageSelected = editor.isActive("image");
        const getImageAlign = () => {
          const { from } = editor.state.selection;
          const node = editor.state.doc.nodeAt(from);
          return node?.attrs.align || "left";
        };
        const currentAlign = isImageSelected ? getImageAlign() : "left";

        return (
          <>
            {!isImageSelected && (
              <button
                key="image-insert"
                type="button"
                onClick={() => {
                  editor.chain().focus().insertImagePlaceholder().run();
                }}
                className={baseClass}
                title="Insert Image"
              >
                <ImageIcon className="h-4 w-4" />
              </button>
            )}
            {isImageSelected && (
              <>
                <button
                  key="image-resize"
                  type="button"
                  onClick={handleImageResize}
                  className={baseClass}
                  title="Resize Image"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <button
                  key="image-align-left"
                  type="button"
                  onClick={() => handleImageAlign("left")}
                  className={`${baseClass} ${
                    currentAlign === "left" ? activeClass : ""
                  }`}
                  title="Align Image Left"
                >
                  <AlignLeft className="h-4 w-4" />
                </button>
                <button
                  key="image-align-center"
                  type="button"
                  onClick={() => handleImageAlign("center")}
                  className={`${baseClass} ${
                    currentAlign === "center" ? activeClass : ""
                  }`}
                  title="Align Image Center"
                >
                  <AlignCenter className="h-4 w-4" />
                </button>
                <button
                  key="image-align-right"
                  type="button"
                  onClick={() => handleImageAlign("right")}
                  className={`${baseClass} ${
                    currentAlign === "right" ? activeClass : ""
                  }`}
                  title="Align Image Right"
                >
                  <AlignRight className="h-4 w-4" />
                </button>
              </>
            )}
          </>
        );
      }
      default:
        return null;
    }
  };

  // Group toolbar elements with separators
  const renderToolbar = () => {
    const buttons: React.ReactNode[] = [];

    finalToolbarElements.forEach((element, index) => {
      // Add separator before certain element groups
      const isTextFormatting = [
        "bold",
        "italic",
        "strikethrough",
        "code",
      ].includes(element);
      const isList = ["bulletList", "orderedList"].includes(element);
      const isBlock = [
        "blockquote",
        "codeBlock",
        "horizontalRule",
        "hardBreak",
      ].includes(element);
      const isHistory = ["undo", "redo"].includes(element);
      const isMedia = element === "image";
      const isAlignment = [
        "alignLeft",
        "alignCenter",
        "alignRight",
        "alignJustify",
      ].includes(element);

      // Add separator before groups (except first element)
      if (index > 0) {
        const prevElement = finalToolbarElements[index - 1] ?? "";
        const prevIsTextFormatting = [
          "bold",
          "italic",
          "strikethrough",
          "code",
        ].includes(prevElement);
        const prevIsList = ["bulletList", "orderedList"].includes(prevElement);
        const prevIsBlock = [
          "blockquote",
          "codeBlock",
          "horizontalRule",
          "hardBreak",
        ].includes(prevElement);
        const prevIsHistory = ["undo", "redo"].includes(prevElement);
        const prevIsMedia = prevElement === "image";
        const prevIsAlignment = [
          "alignLeft",
          "alignCenter",
          "alignRight",
          "alignJustify",
        ].includes(prevElement);

        if (
          (isTextFormatting && !prevIsTextFormatting) ||
          (isList && !prevIsList) ||
          (isBlock && !prevIsBlock) ||
          (isHistory && !prevIsHistory) ||
          (isMedia && !prevIsMedia) ||
          (isAlignment && !prevIsAlignment)
        ) {
          buttons.push(
            <div
              key={`separator-${element}`}
              className="mx-1 h-6 w-px bg-border"
            />,
          );
        }
      }

      buttons.push(renderToolbarButton(element));
    });

    return buttons;
  };

  return (
    <div
      className={`relative rounded-md border border-input bg-background ${className ?? ""}`}
    >
      {finalToolbarElements.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 border-border border-b p-2">
          {renderToolbar()}
        </div>
      )}
      <div className="relative">
        <EditorContent
          editor={editor}
          className="prose prose-sm [&_img[style*='height']]:!h-auto [&_img[style*='width']]:!max-w-none max-h-[600px] min-h-[200px] max-w-none overflow-y-auto focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 [&_img]:my-2 [&_img]:rounded-md"
        />
        {!editor.getText().trim() && (
          <div className="pointer-events-none absolute top-4 left-4 text-muted-foreground text-sm">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
